import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Drawer, Input, Field, Container, Loader, Table } from '@itds/elements/index.js';
import { Colors } from '@itds/Theme';
import H1 from '@brokerage/components/H1';
import NewAgencyDrawer from './NewAgency';
import { useSelector, useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import DOMPurify from 'dompurify';
import {
  fetchAgencies,
  setCurrentPage,
  setSearchQuery,
  clearCache
} from '@redux/brokerageSlices/agencySlice';
import { Api } from '@brokerage/utils/Api';

const List = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { agencies, pagination, loading, currentPage, searchQuery } = useSelector(
    state => state.agencies
  );

  const [name, setName] = useState('');
  const [showNewDemoAgency, toggleNewDemoAgency] = useState(false);
  const [showNewAgency, toggleNewAgency] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchQuery);

  useEffect(() => {
    dispatch(fetchAgencies({ page: currentPage, search: searchQuery }));
  }, [dispatch, currentPage, searchQuery]);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce(value => {
        dispatch(setSearchQuery(value));
      }, 500),
    [dispatch]
  );

  const handleSearchChange = e => {
    const rawValue = e.target.value;
    const trimmedValue = rawValue.trimStart();

    const safeValue = DOMPurify.sanitize(trimmedValue);

    setLocalSearchTerm(safeValue);
    debouncedSetSearchTerm(safeValue);
  };

  const handleSearchKeyDown = e => {
    if (e.key === 'Enter') {
      debouncedSetSearchTerm.cancel();
      dispatch(setSearchQuery(localSearchTerm));
      dispatch(setCurrentPage(1));
      dispatch(clearCache());
    }
  };

  const handlePageChange = newPage => {
    window.scrollTo(0, 0);
    dispatch(setCurrentPage(newPage));
    dispatch(fetchAgencies({ page: newPage, search: searchQuery }));
  };

  const handleNameChange = e => {
    setName(e.target.value);
  };

  const saveDemoAgency = () => {
    window.scrollTo(0, 0);
    Api.saveDemoAgency({ is_demo: true, name }).then(({ data }) => {
      setName('');
      dispatch(fetchAgencies({ page: currentPage, search: searchQuery }));
      toggleNewDemoAgency(false);
      navigate(`/agencies/${data.id}/general`);
    });
  };

  return (
    <Container>
      <Drawer
        alignRight={true}
        footer={
          <Button inline={true} onClick={saveDemoAgency}>
            Create Demo Agency
          </Button>
        }
        onClose={() => toggleNewDemoAgency(false)}
        show={showNewDemoAgency}
        title='New Demo Agency'
      >
        <Field label='Name'>
          <Input onChange={handleNameChange} value={name || ''} />
        </Field>
      </Drawer>

      <H1>Agencies</H1>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}>
        <Input
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          placeholder='Search by name...'
          style={{ width: 300 }}
          value={localSearchTerm}
        />
        <div style={{ marginLeft: 'auto' }}>
          <Button onClick={() => toggleNewAgency(true)}>New Agency</Button>
        </div>
        <div style={{ marginLeft: 10 }}>
          <Button onClick={() => toggleNewDemoAgency(true)}>New Demo Agency</Button>
        </div>
      </div>
      <NewAgencyDrawer onClose={() => toggleNewAgency(false)} show={showNewAgency} />

      {loading ? (
        <Loader />
      ) : (
        <Table
          apiPagination={{ ...pagination, current_page: currentPage, onClick: handlePageChange }}
          columns={[
            { id: 'name', label: 'Name' },
            {
              id: 'is_demo',
              label: 'Is Demo?',
              render: agency =>
                agency.is_demo ? (
                  <i
                    className='mdi mdi-check-bold'
                    style={{ color: Colors.GREEN.hex, fontSize: 20 }}
                  />
                ) : (
                  ''
                ),
              styles: { textAlign: 'center' }
            },
            { id: 'guid', label: 'GUID', styles: { textAlign: 'right' } }
          ]}
          data={agencies}
          onRowClick={agency => navigate(`${agency.id}/general`)}
        />
      )}
    </Container>
  );
};

export default List;
